// ResetPasswordLayout — stránka "/reset-password?token=..." z odkazu v e-mailu.
// Nastaví nové heslo podle tokenu (bez nutnosti znát staré heslo).
// Používá se pro všechny flow (reset / welcome / admin_reset) — stejný ?token=.
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authApi } from '../api/auth'
import { useLoginModalStore } from '../store/useLoginModalStore'

// Blok "odkaz vypršel / chybí" — nabídne vyžádání nového přes forgot-password.
function ExpiredLinkBlock({ navigate }) {
  const [login, setLogin]     = useState('')
  const [sent, setSent]       = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleResend(e) {
    e.preventDefault()
    setLoading(true)
    try {
      await authApi.forgotPassword(login)
    } catch { /* generická odpověď */ }
    setSent(true)
    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-8">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 w-full max-w-sm flex flex-col gap-5">
        <h1 className="text-xl font-bold text-gray-800">Odkaz vypršel nebo je neplatný</h1>
        {sent ? (
          <>
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              Pokud účet existuje, poslali jsme nový odkaz pro nastavení hesla.
            </p>
            <button onClick={() => navigate('/')}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors self-end">
              Zpět na hlavní stránku
            </button>
          </>
        ) : (
          <form onSubmit={handleResend} className="flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              Zadejte uživatelské jméno nebo e-mail — pošleme vám nový odkaz.
            </p>
            <input
              type="text" autoFocus required value={login}
              onChange={e => setLogin(e.target.value)}
              placeholder="Uživatelské jméno nebo e-mail"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => navigate('/')}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
                Zrušit
              </button>
              <button type="submit" disabled={loading}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {loading ? 'Odesílám…' : 'Poslat nový odkaz'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export function ResetPasswordLayout() {
  const navigate = useNavigate()
  const [params]  = useSearchParams()
  const token     = params.get('token') || ''
  const kind      = params.get('kind') || ''
  const showLogin = useLoginModalStore(s => s.show)

  const [newPassword,     setNewPassword]     = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error,   setError]   = useState(null)
  const [expired, setExpired] = useState(false)   // 410 ze serveru
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  if (!token || expired) {
    return <ExpiredLinkBlock navigate={navigate} />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)

    if (newPassword !== confirmPassword) {
      setError('Nové heslo a potvrzení se neshodují')
      return
    }
    if (newPassword.length < 4) {
      setError('Nové heslo musí mít alespoň 4 znaky')
      return
    }

    setLoading(true)
    try {
      await authApi.resetPassword(token, newPassword)
      setSuccess(true)
    } catch (err) {
      if (err.status === 410 || /vypršel|neplatn/i.test(err.message || '')) {
        setExpired(true)
      } else {
        setError(err.message)
      }
    } finally {
      setLoading(false)
    }
  }

  const heading = kind === 'welcome' ? 'Vítejte — nastavte si heslo' : 'Nastavení nového hesla'

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-8">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 w-full max-w-sm flex flex-col gap-5">
        <h1 className="text-xl font-bold text-gray-800">{heading}</h1>

        {success ? (
          <>
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              Heslo bylo úspěšně nastaveno. Nyní se můžete přihlásit.
            </p>
            <button
              onClick={() => { navigate('/'); showLogin() }}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors self-end"
            >
              Přejít na přihlášení
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-600 font-medium">Nové heslo</label>
              <input
                type="password"
                autoComplete="new-password"
                autoFocus
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                minLength={4}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-600 font-medium">Nové heslo znovu</label>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                minLength={4}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <div className="flex gap-3 justify-end mt-1">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Zrušit
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Ukládám…' : 'Nastavit heslo'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
